import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import moment from 'moment';
import axios from 'axios';
import { API_Root } from '../../globalState';
import './AddSingleVehicle.css';
import {
    ceilToThirtyMinutes,
    dateTimeFromDateAndTime,
    endOfScheduleDay,
    hasScheduleSlot,
    maxPickupDateTime,
    minDeliveryDateTime,
    minutesToTurnaround,
    startOfDay,
    timesForDate,
} from './scheduleWindows';

export const EditTripDetails = ({ trip, onSaved, onCancel }) => {
    const [dealNumber, setDealNumber] = useState('');
    const [deliverytime, setDeliveryTime] = useState(null);
    const [fromTime, setFromTime] = useState('');
    const [toTime, setToTime] = useState('');
    const [pickupDate, setPickupDate] = useState(null);
    const [pickupTime, setPickupTime] = useState('');
    const [customNotes, setCustomNotes] = useState('');
    const [selNotes, setSelNotes] = useState([]);
    const [allNotes, setAllNotes] = useState([]);
    const [saving, setSaving] = useState(false);
    const [init, setInit] = useState(false);
    const [staleDeliveryLabel, setStaleDeliveryLabel] = useState(null);
    const [now] = useState(() => new Date());

    const toast = useRef(null);

    const toTurnaroundMinutes = useMemo(
        () => minutesToTurnaround(trip?.TripLocations, trip?.estimatedHours),
        [trip]
    );
    const minDeliveryDt = useMemo(
        () => ceilToThirtyMinutes(minDeliveryDateTime(toTurnaroundMinutes, now)),
        [toTurnaroundMinutes, now]
    );
    const hasDeliveryDate = deliverytime && typeof deliverytime.getFullYear === 'function';
    const computedFromTimes = hasDeliveryDate ? timesForDate(deliverytime, minDeliveryDt) : [];
    const maxPickupDt = useMemo(() => {
        if (!hasDeliveryDate) {
            return null;
        }
        const deliveryDt = fromTime
            ? dateTimeFromDateAndTime(deliverytime, fromTime)
            : endOfScheduleDay(deliverytime);
        return maxPickupDateTime(deliveryDt, toTurnaroundMinutes);
    }, [hasDeliveryDate, deliverytime, fromTime, toTurnaroundMinutes]);
    const hasPickupDate = pickupDate && typeof pickupDate.getFullYear === 'function';
    const computedPickupTimes = hasPickupDate ? timesForDate(pickupDate, now, maxPickupDt || undefined) : [];
    const fromTimeIndex = computedFromTimes.findIndex((t) => t.value === fromTime);
    const computedToTimes = fromTimeIndex >= 0 ? computedFromTimes.slice(fromTimeIndex + 1) : [];
    const pickupEnabled = !!maxPickupDt && hasScheduleSlot(now, maxPickupDt);

    const getPorterNotesList = () => {
        var apiUrl = `${API_Root}api/trip/GetAllPorterNotes`;
        axios.get(apiUrl).then((values) => {
            setAllNotes(values.data);
        });
    }

    const selectFromTime = (val) => {
        setFromTime(val);
        const ind = computedFromTimes.findIndex((t) => t.value === val);
        const rest = ind >= 0 ? computedFromTimes.slice(ind + 1) : [];
        setToTime(rest[0]?.value ?? '');
    };

    const deliveryDateSelected = (dt) => {
        setDeliveryTime(dt);
        setFromTime('');
        setToTime('');
    };

    const pickupDateSelected = (dt) => {
        setPickupDate(dt);
        setPickupTime('');
    };

    const onNotesChange = (e) => {
        let _notes = [...selNotes];

        if (e.checked)
            _notes.push(e.value);
        else
            _notes.splice(_notes.indexOf(e.value), 1);

        setSelNotes(_notes);
    }

    const loadTrip = (t) => {
        setDealNumber(t.dealNumber ?? '');
        setCustomNotes(t.porterNotes ?? '');
        setSelNotes((t.TripPorterNotes || []).map((n) => n.noteId));

        if (t.requestedDeliveryDateTime) {
            const timeFrom = new Date(t.requestedDeliveryDateTime);
            const timeTo = t.requestedDeliveryDateTimeEnd
                ? new Date(t.requestedDeliveryDateTimeEnd)
                : timeFrom;
            setDeliveryTime(timeFrom);
            setFromTime(moment(timeFrom).format('hh:mm A'));
            setToTime(moment(timeTo).format('hh:mm A'));
            if (startOfDay(timeFrom) < startOfDay(minDeliveryDt)) {
                setStaleDeliveryLabel(timeFrom.toLocaleString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                }));
            } else {
                setStaleDeliveryLabel(null);
            }
        } else {
            setDeliveryTime(null);
            setFromTime('');
            setToTime('');
            setStaleDeliveryLabel(null);
        }

        if (t.pickupDateTime) {
            const pTime = new Date(t.pickupDateTime);
            setPickupDate(pTime);
            setPickupTime(moment(pTime).format('hh:mm A'));
        } else {
            setPickupDate(null);
            setPickupTime('');
        }
    }

    useEffect(() => {
        if (!init) {
            getPorterNotesList();
            setInit(true);
        }
    }, [init]);

    useEffect(() => {
        if (trip) {
            loadTrip(trip);
        }
    }, [trip?.id]);

    useEffect(() => {
        if (!hasDeliveryDate) {
            return;
        }
        if (startOfDay(deliverytime) < startOfDay(minDeliveryDt) || computedFromTimes.length === 0) {
            setDeliveryTime(null);
            setFromTime('');
            setToTime('');
            return;
        }
        if (fromTime && !computedFromTimes.some((t) => t.value === fromTime)) {
            setFromTime('');
            setToTime('');
        }
    }, [toTurnaroundMinutes, deliverytime, minDeliveryDt]);

    useEffect(() => {
        if (!hasPickupDate) {
            return;
        }
        const pickupTooEarly = startOfDay(pickupDate) < startOfDay(now);
        const pickupTooLate = maxPickupDt ? startOfDay(pickupDate) > startOfDay(maxPickupDt) : !hasDeliveryDate;
        if (pickupTooEarly || pickupTooLate || computedPickupTimes.length === 0) {
            setPickupDate(null);
            setPickupTime('');
            return;
        }
        if (pickupTime && !computedPickupTimes.some((t) => t.value === pickupTime)) {
            setPickupTime('');
        }
    }, [pickupDate, pickupTime, fromTime, deliverytime, toTurnaroundMinutes, maxPickupDt, now]);

    const getDealNumberClass = () => {
        return (dealNumber) ? "w-12rem text-base text-color surface-overlay p-2 border-1 border-solid surface-border border-round appearance-none outline-none focus:border-primary w-full" :
            "w-12rem text-base text-color surface-overlay p-2 border-1 border-solid border-round appearance-none outline-none focus:border-primary w-full border-red-400";
    }

    const getToTimeClass = () => {
        return (toTime) ? "" : "border-red-400";
    }

    const getFromTimeClass = () => {
        return (fromTime) ? "" : "border-red-400";
    }

    const getDeliveryDateClass = () => {
        return (deliverytime) ? "w-10rem" : "w-10rem border-red-400";
    }

    const canSave = () => {
        return dealNumber &&
            deliverytime &&
            fromTime && fromTime.length > 0 &&
            toTime && toTime.length > 0;
    }

    const saveTripDetails = () => {
        if (!canSave() || !trip) {
            toast.current.show({
                severity: 'warn',
                summary: 'Missing information',
                detail: 'Please provide deal number and a delivery date/time window.',
                life: 3000
            });
            return;
        }

        const dto = {
            ...trip,
            dealNumber: dealNumber,
            requestedDeliveryDateTime: dateTimeFromDateAndTime(deliverytime, fromTime),
            requestedDeliveryDateTimeEnd: dateTimeFromDateAndTime(deliverytime, toTime || fromTime),
            pickupDateTime: (pickupDate && pickupTime) ? dateTimeFromDateAndTime(pickupDate, pickupTime) : null,
            porterNotes: customNotes,
            TripPorterNotes: selNotes.map((n) => { return { noteId: n } })
        };

        setSaving(true);
        var apiUrl = `${API_Root}api/Trip/UpdateTripDetails`;
        axios.post(apiUrl, dto).then((values) => {
            setSaving(false);
            if (values.data === true || values.data === undefined || values.status === 200) {
                toast.current.show({ severity: 'success', summary: 'Trip updated', detail: 'Delivery details were saved.', life: 3000 });
                if (onSaved) {
                    onSaved(dto);
                }
            } else {
                toast.current.show({ severity: 'error', summary: 'Could not save', detail: 'Trip details could not be updated. Please try again.', life: 4000 });
            }
        }).catch(() => {
            setSaving(false);
            toast.current.show({ severity: 'error', summary: 'Could not save', detail: 'Trip details could not be updated. Please try again.', life: 4000 });
        });
    }

    return (
        <>
            <Toast ref={toast} />
            <div className="field flex flex-column">
                <label htmlFor="edit-dealnumber">Deal number <span className="required-mark">*</span></label>
                <input id="edit-dealnumber"
                    value={dealNumber}
                    onChange={(e) => setDealNumber(e.target.value)}
                    placeholder="Sales deal or RO number"
                    className={getDealNumberClass()} />
                <small className="field-hint">Used to link this move to your sales or service records.</small>
            </div>
            <div className="w-full grid">
                <div className="flex flex-column col-12">
                    <div className="field mt-1">
                        <label htmlFor="edit-deliverytime">Requested delivery window <span className="required-mark">*</span></label>
                        <div className="flex flex-wrap align-items-center gap-2">
                            <Calendar minDate={minDeliveryDt} value={deliverytime} onChange={(e) => deliveryDateSelected(e.value)} placeholder="Select date" inputClassName={getDeliveryDateClass()} className="flex-none" />
                            <Dropdown value={fromTime} onChange={(e) => selectFromTime(e.value)} options={computedFromTimes} optionLabel="value"
                                placeholder="From" className={`${getFromTimeClass()} flex-none w-10rem`} />
                            <Dropdown value={toTime} onChange={(e) => setToTime(e.value)} options={computedToTimes} optionLabel="value"
                                placeholder="To" className={`${getToTimeClass()} flex-none w-10rem`} />
                        </div>
                        {staleDeliveryLabel && !deliverytime ? (
                            <small className="field-hint">Previous requested delivery was {staleDeliveryLabel}. Choose a new date.</small>
                        ) : null}
                        <small className="field-hint">When the vehicle must be delivered. Earliest time is now plus the drive time to the last stop before returning home, plus 30 minutes ({minDeliveryDt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}).</small>
                    </div>
                </div>
                <div className="field flex flex-column col-12">
                    <div className="field mt-1">
                        <label htmlFor="edit-pickuptime">Pickup available (optional)</label>
                        <div className="flex flex-wrap align-items-center gap-2">
                            <Calendar minDate={now} maxDate={maxPickupDt || undefined} disabled={!pickupEnabled} value={pickupDate} onChange={(e) => pickupDateSelected(e.value)} placeholder="Select date" inputClassName="w-10rem" className="flex-none" />
                            <Dropdown value={pickupTime} onChange={(e) => setPickupTime(e.value)} options={computedPickupTimes} optionLabel="value"
                                placeholder="Time" disabled={!hasPickupDate} className="flex-none w-10rem" />
                        </div>
                        <small className="field-hint">When the driver can pick up the vehicle. Must be no earlier than now and no later than the requested delivery time minus the estimated drive time plus 30 minutes.</small>
                    </div>
                </div>
            </div>
            <div className="flex flex-column">
                <div className="flex flex-wrap justify-content-left">
                    <label htmlFor="">Standard notes for driver</label>
                    <ScrollPanel style={{ width: '100%', height: '80px' }}>
                        {allNotes.map((n, i) => {
                            return (<div key={i}>
                                <Checkbox
                                    value={n.id}
                                    onChange={onNotesChange}
                                    checked={selNotes.includes(n.id)} />
                                <span className="ml-2">{n.note}</span>
                            </div>);
                        })}
                    </ScrollPanel>
                    <div className="field w-full">
                        <label htmlFor="edit-customnotes">Custom note</label>
                        <div>
                            <InputTextarea autoResize
                                id="edit-customnotes"
                                value={customNotes}
                                onChange={(e) => setCustomNotes(e.target.value)}
                                rows={5}
                                cols={80}
                                placeholder="Any special instructions for the driver..."
                                className="w-full"
                                style={{ minHeight: '7rem' }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex justify-content-end gap-2 mt-3">
                <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => onCancel && onCancel()} disabled={saving} />
                <Button label="Save" icon="pi pi-check" onClick={() => saveTripDetails()} disabled={!canSave() || saving} loading={saving} />
            </div>
        </>
    );
};
